/**
 * 汎用性の高い関数、クラス、インターフェイスを定義する。
 */

import { date as dateUtil } from 'quasar';
import { sha1 as hash } from 'object-hash'; // https://www.npmjs.com/package/@types/object-hash

/**
 * 数値を指定した基数で変換した文字列に対し、前方をゼロ埋めした文字列を作成する。
 * @param value 数値
 * @param radix 基数（https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Number/toString）
 * @param length ゼロ埋めした文字列の長さ
 * @example zeroPadding(18, 16, 6) => '000012'
 */
export const zeroPadding = (
  value: number,
  radix: number | undefined,
  length: number
): string => {
  return (Array(length).join('0') + value.toString(radix)).slice(-length);
};

/**
 * 常時表示ボタンの表示個数に応じた、各要素のクラス名配列を返す。
 * @param length 常時表示ボタンの表示個数(1-12)
 * @returns 各要素のクラス名配列
 */
export const getRegularClassNames = (length: number) => {
  if (length <= 1) {
    return ['col-12', 'full-height'];
  } else if (length <= 2) {
    return ['col-12', 'height-50x'];
  } else if (length <= 3) {
    return ['col-12', 'height-33x'];
  } else if (length <= 4) {
    return ['col-6', 'height-50x'];
  } else if (length <= 6) {
    return ['col-6', 'height-33x'];
  } else if (length <= 9) {
    return ['col-4', 'height-33x'];
  } else {
    return ['col-4', 'height-25x'];
  }
};

/**
 * 日付・時刻のフォーマッター
 */
export const dateFormatter = {
  /**
   * サーバーと送受信する際のdate-time文字列フォーマットを取得する。
   * @returns APIの日時文字列フォーマット
   * @see {@link https://quasar.dev/quasar-utils/date-utils#Format-for-display}
   */
  getApiDateTimeFormat() {
    return 'YYYY-MM-DDTHH:mm:ss';
  },

  /**
   * サーバーから受信したdate-time文字列をDateオブジェクトに変換する。
   * フォーマットは"YYYY-MM-DDTHH:mm:ss"。
   * サーバー側ではタイムゾーンを管理しないため、クライアント側で矛盾のないように取り扱う。
   * @param dateTime "YYYY-MM-DDTHH:mm:ss"形式の時刻文字列
   * @example buildDateFromApiDateTime('2021-01-28T23:41:21') => Date（ローカル時刻で2021年1月28日23時41分21秒）
   * @returns Dateオブジェクト
   */
  buildDateFromApiDateTime(dateTime: string) {
    return dateUtil.extractDate(dateTime, this.getApiDateTimeFormat());
  },

  /**
   * Dateオブジェクトをサーバーに送信するためのdate-time文字列に変換する。
   * フォーマットは"YYYY-MM-DDTHH:mm:ss"。
   * サーバー側ではタイムゾーンを管理しないため、クライアント側で矛盾のないように取り扱う。
   * @param date Dateオブジェクト
   * @example formatApiDateTime(Date（ローカル時刻で2021年1月28日23時41分21秒）) => '2021-01-28T23:41:21'
   * @returns "YYYY-MM-DDTHH:mm:ss"形式の時刻文字列
   */
  formatApiDateTime(date: Date) {
    return dateUtil.formatDate(date, this.getApiDateTimeFormat());
  },

  /**
   * 画面表示用の日付文字列フォーマットを取得する。
   * @returns 画面表示用の日付文字列フォーマット
   * @see {@link https://quasar.dev/quasar-utils/date-utils#Format-for-display}
   */
  getDisplayDateFormat() {
    return 'YYYY/MM/DD(ddd)';
  },

  /**
   * 画面表示用の時刻文字列フォーマットを取得する。
   * @returns 画面表示用の時刻文字列フォーマット
   * @see {@link https://quasar.dev/quasar-utils/date-utils#Format-for-display}
   */
  getDisplayTimeFormat() {
    return 'HH:mm';
  },

  /**
   * 画面表示用の日付+時刻文字列フォーマットを取得する。
   * @returns 画面表示用の日付+時刻文字列フォーマット
   * @see {@link https://quasar.dev/quasar-utils/date-utils#Format-for-display}
   */
  getDisplayDatetimeFormat() {
    return `${this.getDisplayDateFormat()} ${this.getDisplayTimeFormat()}`;
  },

  /**
   * Dateオブジェクトを画面表示用の日付文字列に変換する。
   * @param datetime Dateオブジェクト
   * @example formatDisplayDate(Date（ローカル時刻で2021年1月28日23時41分21秒）) => '2021/1/28(木)'
   * @returns 画面表示用の日付文字列
   */
  formatDisplayDate(datetime: Date) {
    return dateUtil.formatDate(datetime, this.getDisplayDateFormat());
  },

  /**
   * Dateオブジェクトを画面表示用の時刻文字列に変換する。
   * @param datetime Dateオブジェクト
   * @example formatDisplayTime(Date（ローカル時刻で2021年1月28日23時41分21秒）) => '23:41'
   * @returns 画面表示用の時刻文字列
   */
  formatDisplayTime(datetime: Date) {
    return dateUtil.formatDate(datetime, this.getDisplayTimeFormat());
  },

  /**
   * Dateオブジェクトを画面表示用の日付+時刻文字列に変換する。
   * @param datetime Dateオブジェクト
   * @example formatDisplayDate(Date（ローカル時刻で2021年1月28日23時41分21秒）) => '2021/1/28(木) 23:41'
   * @returns 画面表示用の日付+時刻文字列
   */
  formatDisplayDatetime(datetime: Date) {
    return dateUtil.formatDate(datetime, this.getDisplayDatetimeFormat());
  },

  /**
   * 画面表示用の日付文字列をDateオブジェクトに変換する。
   * @param date 画面表示用のの時刻文字列
   * @example buildDateFromDisplayDate('2021/01/28(木)') => Date（ローカル時刻で2021年1月28日0時0分0秒）
   * @returns Dateオブジェクト
   */
  buildDateFromDisplayDate(date: string) {
    return dateUtil.extractDate(date, this.getDisplayDateFormat());
  },

  /**
   * 画面表示用の日付+時刻文字列をDateオブジェクトに変換する。
   * @param date 画面表示用のの時刻文字列
   * @example buildDateFromDisplayDatetime('2021/01/28(木) 23:41') => Date（ローカル時刻で2021年1月28日23時41分0秒）
   * @returns Dateオブジェクト
   */
  buildDateFromDisplayDatetime(datetime: string) {
    return dateUtil.extractDate(datetime, this.getDisplayDatetimeFormat());
  },
};

/**
 * 2つのDateオブジェクトが同じ日付か判定する。
 * @param date1 Dateオブジェクト1
 * @param date2 Dateオブジェクト2
 * @example isSameDate(date1, date2) => true or false
 * @returns 同じ日付ならtrue、異なる日付ならfalse
 */
export const isSameDate = (date1: Date, date2: Date) =>
  dateUtil.isSameDate(date1, date2, 'year') &&
  dateUtil.isSameDate(date1, date2, 'month') &&
  dateUtil.isSameDate(date1, date2, 'day');

/**
 * Dateオブジェクトの時刻情報を0にする。
 * @param date Dateオブジェクト
 * @returns 時刻情報が0になったDateオブジェクト
 */
export const truncateTime = (date: Date) =>
  dateUtil.adjustDate(date, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

/**
 * ハッシュ値付きオブジェクト。
 *
 * オブジェクト全体のハッシュ値を計算し、オブジェクト本体と一緒に保持します。
 * オブジェクトはReadonlyになります。（変更時にハッシュ値を再計算する機構を用意していないため）
 */
export class ObjectWithHash<T> {
  readonly hashValue: string;
  readonly object: Readonly<T>;

  constructor(object: T) {
    this.hashValue = hash(object);
    this.object = object;
  }
}
